import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Application } from '@/types/database.types';
import { 
  processAdditionalContacts, 
  processPayrollSchedules, 
  ensureStringArray,
  normalizeCorporateStructure,
  processHealthPlans
} from '@/utils/application-data-utils';
import type { Json } from '@/integrations/supabase/types';

export const useApplicationData = (applicationId: string | null, fetchAll: boolean = false) => {
  const [data, setData] = useState<Application | Application[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching application data, applicationId:', applicationId, 'fetchAll:', fetchAll);
        
        // If fetchAll is true, get all applications
        if (fetchAll) {
          const { data: allApplications, error: applicationsError } = await supabase
            .from('applications')
            .select('*')
            .order('created_at', { ascending: false });
          
          if (applicationsError) {
            throw applicationsError;
          }
          
          if (!allApplications) {
            setData([]);
            return;
          }
          
          // Process all applications
          const processedApplications = allApplications.map(item => ({
            ...item,
            additional_contacts: processAdditionalContacts(item.additional_contacts as Json),
            additional_schedules: processPayrollSchedules(item.additional_schedules as Json[]),
            health_plans: processHealthPlans(item.health_plans as Json),
            desired_medical_carriers: ensureStringArray(item.desired_medical_carriers),
            desired_ancillary_carriers: ensureStringArray(item.desired_ancillary_carriers),
            other_documents: ensureStringArray(item.other_documents),
            corporate_structure: normalizeCorporateStructure(item.corporate_structure),
            states_with_employees: ensureStringArray(item.states_with_employees),
            top_3_peo_needs: ensureStringArray(item.top_3_peo_needs),
            preferred_peos: ensureStringArray(item.preferred_peos),
            excluded_peos: ensureStringArray(item.excluded_peos),
            states_of_operation: ensureStringArray(item.states_of_operation)
          })) as Application[];
          
          setData(processedApplications);
          console.log(`Fetched ${processedApplications.length} applications`);
        } 
        // Otherwise, fetch a specific application by ID
        else if (applicationId) {
          const { data: singleApplication, error: applicationError } = await supabase
            .from('applications')
            .select('*')
            .eq('id', applicationId)
            .maybeSingle();
          
          if (applicationError) {
            throw applicationError;
          }
          
          if (!singleApplication) {
            setData(null);
            return;
          }
          
          // Process the single application
          const processedData = {
            ...singleApplication,
            additional_contacts: processAdditionalContacts(singleApplication.additional_contacts as Json),
            additional_schedules: processPayrollSchedules(singleApplication.additional_schedules as Json[]),
            health_plans: processHealthPlans(singleApplication.health_plans as Json),
            desired_medical_carriers: ensureStringArray(singleApplication.desired_medical_carriers),
            desired_ancillary_carriers: ensureStringArray(singleApplication.desired_ancillary_carriers),
            other_documents: ensureStringArray(singleApplication.other_documents),
            corporate_structure: normalizeCorporateStructure(singleApplication.corporate_structure),
            states_with_employees: ensureStringArray(singleApplication.states_with_employees),
            top_3_peo_needs: ensureStringArray(singleApplication.top_3_peo_needs),
            preferred_peos: ensureStringArray(singleApplication.preferred_peos),
            excluded_peos: ensureStringArray(singleApplication.excluded_peos),
            states_of_operation: ensureStringArray(singleApplication.states_of_operation)
          } as Application;
          
          setData(processedData);
          console.log('Fetched application:', processedData.id);
        } else {
          setData(null);
        }
      } catch (err) {
        console.error('Error fetching application data:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [applicationId, fetchAll]);

  return { data, isLoading, error };
};
